import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tooltip,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { CirclePlus, LoaderCircle } from "lucide-react";
import React, {
  type ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SectionHeader } from "../components";
import { DEFAULT_CATEGORY, useFilesContext } from "./FilesContext";
import { FilesGrid } from "./components/FilesGrid";
import { NewFile } from "./components/NewFile";
import { useFileCategories } from "./network/useFileCategories";

export const Files = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [isSelectingFile, setIsSelectingFile] = useState(false);

  const { files, selectedCategory, setSelectedCategory } = useFilesContext();
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useFileCategories();

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of files ?? []) {
      const code = f.type?.coding?.[0]?.code;
      if (!code) continue;
      counts.set(code, (counts.get(code) ?? 0) + 1);
    }
    return counts;
  }, [files]);

  const categoriesWithCounts = useMemo(
    () =>
      (categoriesData?.categories ?? [])
        .map((c) => ({ ...c, count: categoryCounts.get(c.id) ?? 0 }))
        .filter((c) => c.count > 0),
    [categoriesData?.categories, categoryCounts],
  );

  useEffect(() => {
    if (selectedCategory === DEFAULT_CATEGORY) return;
    const exists = categoriesWithCounts.some((c) => c.id === selectedCategory);
    if (!exists) {
      setSelectedCategory(DEFAULT_CATEGORY);
    }
  }, [selectedCategory, categoriesWithCounts, setSelectedCategory]);

  useEffect(() => {
    if (!isSelectingFile) return;

    const handleFocus = () => {
      setTimeout(() => {
        setIsSelectingFile(false);
      }, 100);
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [isSelectingFile]);

  const handleUploadClick = () => {
    trackEvent(TRACKERS.ADD_FILE);
    setIsSelectingFile(true);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsSelectingFile(false);
    if (!e.target.files) return;
    setNewFile(e.target.files[0]);
  };

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Files"
        actionButtons={[
          <React.Fragment key="upload-file">
            <Tooltip
              tooltipContentText="Add"
              triggerClassName="leading-[0px]"
              side="bottom"
              key="upload-file"
            >
              <button
                key="upload-file"
                data-testid="upload-file"
                type="button"
                onClick={handleUploadClick}
                disabled={isSelectingFile}
              >
                {isSelectingFile ? (
                  <LoaderCircle className="stroke-link w-4 h-4 animate-spin" />
                ) : (
                  <CirclePlus className="stroke-link w-4 h-4" />
                )}
              </button>
            </Tooltip>
            <input
              key="upload-file-input"
              data-testid="upload-file-input"
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </React.Fragment>,
        ]}
      />

      <div className="mt-3">
        {isLoadingCategories && !newFile && <Skeleton className="h-8 w-52" />}
        {!isLoadingCategories && !newFile && (
          <Select
            value={selectedCategory}
            onValueChange={(value) => {
              trackEvent(TRACKERS.FILTER_CATEGORIES);
              setSelectedCategory(value);
            }}
          >
            <SelectTrigger aria-label="filter-category" className="max-w-52">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={DEFAULT_CATEGORY} rightSlot={files?.length}>
                  All categories
                </SelectItem>
                <SelectSeparator />
                {categoriesWithCounts.map(({ id, type, count }) => (
                  <SelectItem key={id} value={id} rightSlot={count}>
                    {type}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
      </div>
      {newFile ? (
        <NewFile
          onCancel={() => setNewFile(null)}
          onSave={() => setNewFile(null)}
          file={newFile}
          onChange={handleFileChange}
        />
      ) : (
        <FilesGrid />
      )}
    </div>
  );
};
